import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { PlusSmIcon, CogIcon } from '@heroicons/react/outline';

import PageContainer from '../../components/Containers/PageContainer';
import Button from '../../components/Button';
import TableContainer from '../../components/TableContainer';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ClientSessionReviewStatus,
  ProviderUserRole,
} from '../../../generated/graphql';

import ReviewSessionModal from './components/modals/ReviewSessionModal';
import UpdateSessionModal from './components/modals/UpdateSessionModal';
import CreateSessionModal from './components/modals/CreateSessionModal';
import SessionsSettingsModal from './components/modals/SessionsSettingsModal';
import { SESSION_STATES } from './utils/constants';

import { useSessionStatus } from './hooks/useSessionStatus';
import { useSessionModal } from './hooks/useSessionModal';
import { SessionModalMode } from './types';
import { useSessionsData } from './hooks/useSessionData';
import { usePageStore } from './hooks/usePageStore';
import { useSessionsStore } from './hooks/useSessionStore';
import { logger } from '../../../lib/logger';

const ClientSessionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { showUpgradeBanner, authedProviderUser } = useAuth();
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const canAccessSettings =
    authedProviderUser &&
    !authedProviderUser.isExternalCollaborator &&
    (authedProviderUser.role === ProviderUserRole.Owner ||
      authedProviderUser.role === ProviderUserRole.Administrator);
  const setShowUpgradeBanner = usePageStore(
    (state) => state.setShowUpgradeBanner,
  );
  const setEditHandler = usePageStore((state) => state.setEditHandler);
  const setUpdateHandler = usePageStore((state) => state.setUpdateHandler);
  const setStatus = usePageStore((state) => state.setStatus);

  const { modalType, selectedSession, openModal, openCreateModal, closeModal } =
    useSessionModal();

  const { setCurrentStatus, setPathStatus, fetchSessionsWithCount } =
    useSessionsStore();

  const currentState = useSessionStatus(location.pathname);
  const filterStatus = currentState.status;
  const pageTitle = currentState.title;

  const {
    refetch: refetchSessions,
    pendingCount,
    confirmedCount,
    dismissedCount,
  } = useSessionsData();

  const handleReview = async (session: any) => {
    await openModal(session.id);
  };

  const handleEdit = async (session: any) => {
    await openModal(session.id);
  };

  const handleModalClose = () => {
    closeModal();
    if (sessionId) {
      navigate(-1);
    }
  };

  const handleModalSuccess = async (createdSession?: any) => {
    await refetchSessions();

    // If a session was just created, open the review modal for it
    if (createdSession) {
      await openModal(createdSession.id);
    }
  };

  useEffect(() => {
    setStatus(filterStatus);
    setShowUpgradeBanner(showUpgradeBanner);
    setPathStatus(location.pathname);

    const editHandler =
      filterStatus === ClientSessionReviewStatus.PendingReview
        ? handleReview
        : handleEdit;

    setEditHandler(editHandler);
    setUpdateHandler(handleEdit);
  }, [filterStatus, showUpgradeBanner, location.pathname, setPathStatus]);

  useEffect(() => {
    if (sessionId) {
      openModal(sessionId).catch((error) => {
        logger.error('Error opening session:', error);
        navigate(-1);
      });
    }
  }, [sessionId, openModal, navigate]);

  useEffect(() => {
    if (!modalType && !selectedSession) {
      fetchSessionsWithCount();
    }
  }, [modalType, selectedSession, fetchSessionsWithCount]);

  return (
    <PageContainer
      containerClassName={classNames(
        showUpgradeBanner
          ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))] overflow-y-hidden'
          : 'h-[calc(100vh-var(--top-nav-height))] overflow-y-hidden',
      )}
    >
      {selectedSession && modalType === SessionModalMode.REVIEW && (
        <ReviewSessionModal
          clientSession={selectedSession}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
      {selectedSession && modalType === SessionModalMode.UPDATE && (
        <UpdateSessionModal
          clientSession={selectedSession}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
      {modalType === SessionModalMode.CREATE && (
        <CreateSessionModal
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
      {showSettingsModal && (
        <SessionsSettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
      <div className={classNames('h-full opacity-100 transition duration-300')}>
        <div className="mb-4 flex h-[48px] flex-row justify-between">
          <div className="flex flex-col items-start">
            <div className="font-serif text-subtitle text-green-150">
              {pageTitle}
            </div>
            <div className="text-caption text-neutral-125">
              {currentState.description}
            </div>
          </div>
          <div className="flex flex-row items-center gap-x-5">
            {canAccessSettings && (
              <Button
                title="Settings"
                IconComponent={CogIcon}
                iconPosition="left"
                theme="secondary"
                onClick={() => setShowSettingsModal(true)}
              />
            )}
            <Button
              title="Create"
              IconComponent={PlusSmIcon}
              iconPosition="left"
              onClick={openCreateModal}
            />
          </div>
        </div>
        <div className="mb-4 flex h-[56px] w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center justify-start">
            {/* Left-side Container */}
          </div>
          <div className="flex flex-row items-center justify-end gap-x-2">
            {Object.values(SESSION_STATES).map((state) => {
              const count =
                state === SESSION_STATES.PENDING
                  ? pendingCount
                  : state === SESSION_STATES.CONFIRMED
                  ? confirmedCount
                  : dismissedCount;
              return (
                <button
                  key={state.path}
                  className={classNames(
                    'flex flex-row items-center justify-start rounded-full text-caption text-neutral-125',
                    'px-3 py-1.5',
                    'h-[30px]',
                    location.pathname.includes(state.path)
                      ? 'bg-neutral-50'
                      : 'hover:bg-neutral-25',
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentStatus(state.status);
                    navigate(state.path);
                  }}
                >
                  <span>{state.label}</span>
                  <div className="ml-2 -mr-3 rounded-full bg-neutral-75 px-2.5 py-1 font-medium">
                    {count}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <TableContainer containerClassName="overscroll-contain">
          <Outlet
            context={{
              onReview: handleReview,
              onEdit: handleEdit,
              onModalClose: handleModalClose,
              modalType,
              selectedSession,
            }}
          />
        </TableContainer>
      </div>
    </PageContainer>
  );
};

export default ClientSessionsPage;
